import apiClient from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import type {
  Organization,
  OrganizationDetail,
  OrganizationDomain,
  OrganizationMember,
  OrganizationInvitation,
  OrganizationCredits,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  InviteMemberRequest,
  UpdateMemberRoleRequest,
  TransferOwnershipRequest,
  SchedulerStatus,
  UserProfile,
  OrgSettings,
  UpdateOrgSettingsRequest,
} from '../types';

/**
 * Organizations API client
 */
export const organizationsApi = {
  // === Organization Core Operations ===

  /**
   * List all organizations the current user belongs to
   */
  listOrganizations: async (): Promise<Organization[]> => {
    const response = await apiClient.get(API_ENDPOINTS.ORGANIZATIONS.LIST);
    return response.data;
  },

  /**
   * Get full details for a specific organization
   */
  getOrganization: async (id: string): Promise<OrganizationDetail> => {
    const response = await apiClient.get(API_ENDPOINTS.ORGANIZATIONS.BY_ID(id));
    return response.data;
  },

  /**
   * Create a new organization
   */
  createOrganization: async (data: CreateOrganizationRequest): Promise<Organization> => {
    const response = await apiClient.post(API_ENDPOINTS.ORGANIZATIONS.CREATE, data);
    return response.data;
  },

  /**
   * Update an existing organization
   */
  updateOrganization: async (
    id: string,
    data: UpdateOrganizationRequest
  ): Promise<Organization> => {
    const response = await apiClient.patch(API_ENDPOINTS.ORGANIZATIONS.BY_ID(id), data);
    return response.data;
  },

  /**
   * Delete an organization
   */
  deleteOrganization: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.ORGANIZATIONS.BY_ID(id));
  },

  // === Member Management ===

  /**
   * List all members of an organization
   */
  listMembers: async (id: string): Promise<OrganizationMember[]> => {
    const response = await apiClient.get(API_ENDPOINTS.ORGANIZATIONS.MEMBERS(id));
    return response.data;
  },

  /**
   * Update a member's role
   */
  updateMemberRole: async (
    orgId: string,
    userId: string,
    data: UpdateMemberRoleRequest
  ): Promise<OrganizationMember> => {
    const response = await apiClient.post(
      API_ENDPOINTS.ORGANIZATIONS.MEMBER_ROLE(orgId, userId),
      data
    );
    return response.data;
  },

  /**
   * Remove a member from an organization
   */
  removeMember: async (orgId: string, userId: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.ORGANIZATIONS.REMOVE_MEMBER(orgId, userId));
  },

  /**
   * Leave an organization
   */
  leaveOrganization: async (orgId: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.ORGANIZATIONS.LEAVE(orgId));
  },

  /**
   * Transfer organization ownership
   */
  transferOwnership: async (orgId: string, data: TransferOwnershipRequest): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.ORGANIZATIONS.TRANSFER_OWNERSHIP(orgId), data);
  },

  // === Domain Verification ===

  /**
   * List organization domains
   */
  listDomains: async (orgId: string): Promise<OrganizationDomain[]> => {
    const response = await apiClient.get(API_ENDPOINTS.ORGANIZATIONS.DOMAINS(orgId));
    return response.data;
  },

  /**
   * Add a new domain to the organization
   */
  addDomain: async (orgId: string, domain: string): Promise<OrganizationDomain> => {
    const response = await apiClient.post(API_ENDPOINTS.ORGANIZATIONS.DOMAINS(orgId), {
      domain,
    });
    return response.data;
  },

  /**
   * Verify an organization domain
   */
  verifyDomain: async (orgId: string, domainId: string): Promise<OrganizationDomain> => {
    const response = await apiClient.post(
      API_ENDPOINTS.ORGANIZATIONS.DOMAIN_VERIFY(orgId, domainId)
    );
    return response.data;
  },

  /**
   * Remove a domain from the organization
   */
  removeDomain: async (orgId: string, domainId: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.ORGANIZATIONS.DOMAIN_DELETE(orgId, domainId));
  },

  // === Invitation Management ===

  /**
   * List pending invitations for an organization
   */
  listInvitations: async (id: string): Promise<OrganizationInvitation[]> => {
    const response = await apiClient.get(API_ENDPOINTS.ORGANIZATIONS.INVITES(id));
    return response.data;
  },

  /**
   * Create a new invitation
   */
  createInvitation: async (
    orgId: string,
    data: InviteMemberRequest
  ): Promise<OrganizationInvitation> => {
    const response = await apiClient.post(API_ENDPOINTS.ORGANIZATIONS.INVITES(orgId), data);
    return response.data;
  },

  /**
   * Cancel a pending invitation
   */
  cancelInvitation: async (orgId: string, inviteId: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.ORGANIZATIONS.CANCEL_INVITE(orgId, inviteId));
  },

  /**
   * Accept an invitation
   */
  acceptInvitation: async (inviteId: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.ORGANIZATIONS.ACCEPT_INVITE(inviteId));
  },

  // === Credits ===

  /**
   * Get organization credits
   */
  getOrganizationCredits: async (orgId: string): Promise<OrganizationCredits> => {
    const response = await apiClient.get(API_ENDPOINTS.ORGANIZATIONS.CREDITS(orgId));
    return response.data;
  },

  // === Scheduler ===

  /**
   * Get live per-org scheduler status. Includes current slot count,
   * queue depth, policy, and overage bill-rate. scheduler_mode="tier_default"
   * means no slot has been seeded yet; returns tier-default constants.
   */
  getSchedulerStatus: async (orgId: string): Promise<SchedulerStatus> => {
    const response = await apiClient.get(API_ENDPOINTS.ORGANIZATIONS.SCHEDULER_STATUS(orgId));
    return response.data;
  },

  // === Current User Profile ===

  /**
   * Fetch the current user's profile (signup grant, payment method flags).
   */
  getMyProfile: async (): Promise<UserProfile> => {
    const response = await apiClient.get(API_ENDPOINTS.USERS.ME_PROFILE);
    return response.data;
  },

  // === Org Settings (model roles) ===

  /** Fetch per-org settings (model_roles overrides). */
  getOrgSettings: async (orgId: string): Promise<OrgSettings> => {
    const response = await apiClient.get(API_ENDPOINTS.ORGANIZATIONS.SETTINGS(orgId));
    return response.data;
  },

  /**
   * PATCH-merge org settings. Keys present in model_roles replace stored values.
   * Key="" removes the override (falls through to platform env / last-resort).
   * Keys absent from the body are unchanged.
   */
  updateOrgSettings: async (orgId: string, payload: UpdateOrgSettingsRequest): Promise<OrgSettings> => {
    const response = await apiClient.put(API_ENDPOINTS.ORGANIZATIONS.SETTINGS(orgId), payload);
    return response.data;
  },
};
